import React, { FC, useEffect, useState } from 'react';
import { IconButton, Menu, MenuItem, Tooltip } from '@material-ui/core';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import { useScope } from '@/hooks/useScope';
import { StaffSchedulePdfGenerator, RoomWithProcedureFlag } from './versions/v1/StaffSchedulePdfGenerator';
import Scope from '@/types/Scope';
import { generateSchedulePdf, generateWeekSchedulePdf } from './versions/schedulePDFGenerator';
import { Room } from '@/types/Room';
import { schedulePdfsOptions } from '../../../../inputs/SchedulePdfsMultipleSelectInput';
import { useLazyQuery } from '@apollo/client';
import { getProceduresFromToSubscription } from '@/graph/procedures';
import { format, startOfWeek, addDays } from 'date-fns';

const PDFScheduleButton: FC<{ date: Date; staffPerRoom: RoomWithProcedureFlag[]; operationRooms?: Room[] }> = ({
  date,
  staffPerRoom,
  operationRooms,
}) => {
  const scope: Scope = useScope();
  const schedulePDFs = scope?.hospital?.modules?.schedulePDFs;
  const hospitalName = scope?.hospital?.name || '';

  const [anchorEl, setAnchorEl] = useState<
    (EventTarget & HTMLButtonElement) | (EventTarget & HTMLAnchorElement) | null
  >(null);

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement> | React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const weekStart = startOfWeek(date, { weekStartsOn: 0 });
  const weekEnd = addDays(weekStart, 6);

  const weekStartFormatted = format(weekStart, 'YYYY-MM-DD');
  const weekEndFormatted = format(weekEnd, 'YYYY-MM-DD');
  const [fetchWeekProcedures, { data: weekData }] = useLazyQuery(getProceduresFromToSubscription, {
    fetchPolicy: 'cache-first',
  });

  const [pendingWeekPdf, setPendingWeekPdf] = useState(false);

  /*
   * NOTE: We intentionally suppress react-hooks/exhaustive-deps for the effect below.
   *
   * Why: The effect references values such as hospitalName, date, staffPerRoom,
   * operationRooms, and the generator function. Including all of these in the
   * dependency array would cause the effect to re-run on unrelated prop/state changes,
   * which could unintentionally re-generate and re-download the PDF.
   *
   * Intended behavior: Run exactly when a user-initiated Week Schedule export is pending
   * (pendingWeekPdf === true) and the week procedures arrive via weekData. Therefore, we
   * depend only on [pendingWeekPdf, weekData].
   *
   * Alternatives if stricter linting is desired:
   * - Snapshot the arguments needed for PDF generation into a ref/state at click time
   *   and have the effect depend on that snapshot + weekData only.
   * - Generate immediately when data is already cached, and for fresh fetches trigger
   *   generation via a completion path rather than by effect dependencies.
   */
  useEffect(() => {
    if (pendingWeekPdf && weekData?.proceduresFromTo) {
      generateWeekSchedulePdf('Week Schedule')?.(
        hospitalName,
        date,
        staffPerRoom,
        operationRooms || [],
        weekData.proceduresFromTo
      );
      setPendingWeekPdf(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingWeekPdf, weekData]);

  if (!schedulePDFs || (schedulePDFs || []).length === 0) {
    return (
      <Tooltip title={'Download Staff Schedule'}>
        <IconButton onClick={() => StaffSchedulePdfGenerator(hospitalName, date, staffPerRoom)} color="primary">
          <PictureAsPdfIcon />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <>
      <Tooltip title={'Pick Staff Schedule to Download'}>
        <IconButton onClick={handleClick} color="primary">
          <PictureAsPdfIcon />
        </IconButton>
      </Tooltip>
      <Menu id="simple-menu" anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleClose}>
        {schedulePdfsOptions
          .filter(e => !!(schedulePDFs || [])?.find((el: { value: string }) => e?.value === el?.value))
          .map(e => (
            <MenuItem
              key={e.value}
              onClick={() => {
                if (e.value === 'Week Schedule') {
                  if (weekData?.proceduresFromTo) {
                    generateWeekSchedulePdf(e.value)?.(
                      hospitalName,
                      date,
                      staffPerRoom,
                      operationRooms || [],
                      weekData.proceduresFromTo
                    );
                  } else {
                    setPendingWeekPdf(true);
                    fetchWeekProcedures({
                      variables: {
                        from: weekStartFormatted,
                        to: weekEndFormatted,
                        isCanceled: false,
                      },
                    });
                  }
                } else {
                  generateSchedulePdf(e.value)?.(hospitalName, date, staffPerRoom, operationRooms || []);
                }
                handleClose();
              }}
            >
              {e.label}
            </MenuItem>
          ))}
      </Menu>
    </>
  );
};

export default PDFScheduleButton;
